import { useState, useCallback, useMemo } from 'react';
import {
  tokens,
  makeStyles,
  shorthands,
  Text,
} from '@fluentui/react-components';
import {
  PersonSearchRegular,
  KeyRegular,
  ShieldKeyholeRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';
import type { EntraUserDetails, AdminIdentity } from '../types';
import { AppHeader } from '../components';
import { UserLookup } from '../features/userLookup';
import { PinGenerator } from '../features/pinGenerator';
import { ProvisioningForm } from '../features/provisioning';

const useStyles = makeStyles({
  layout: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    ...shorthands.padding('24px', '32px'),
    maxWidth: '960px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  pageTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '4px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  heading: {
    fontSize: '24px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  stepper: {
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    ...shorthands.padding('16px', '24px'),
    backgroundColor: '#fff',
    ...shorthands.borderRadius('12px'),
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('0', '16px'),
    flexShrink: 0,
  },
  stepIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    ...shorthands.borderRadius('50%'),
    fontSize: '14px',
    flexShrink: 0,
  },
  stepIconActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: '#fff',
  },
  stepIconDone: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: '#fff',
  },
  stepIconPending: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground3,
  },
  stepLabel: {
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  stepLabelActive: {
    color: tokens.colorBrandForeground1,
  },
  stepLabelDone: {
    color: tokens.colorPaletteGreenForeground1,
  },
  stepLabelPending: {
    color: tokens.colorNeutralForeground3,
  },
  stepConnector: {
    flexGrow: 1,
    height: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
    marginLeft: '8px',
    marginRight: '8px',
    minWidth: '24px',
  },
  stepConnectorDone: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
});

interface MainPageProps {
  admin: AdminIdentity;
  onSignOut: () => void;
}

export function MainPage({ admin, onSignOut }: MainPageProps) {
  const styles = useStyles();
  const [selectedUser, setSelectedUser] = useState<EntraUserDetails | null>(null);
  const [pin, setPin] = useState('');
  const [provisioned, setProvisioned] = useState(false);

  const currentStep = useMemo(() => {
    if (provisioned) return 3;
    if (pin) return 2;
    if (selectedUser) return 1;
    return 0;
  }, [selectedUser, pin, provisioned]);

  const handleReset = useCallback(() => {
    setSelectedUser(null);
    setPin('');
    setProvisioned(false);
  }, []);

  const steps = [
    { label: 'Select User', icon: <PersonSearchRegular fontSize={14} /> },
    { label: 'Generate PIN', icon: <KeyRegular fontSize={14} /> },
    { label: 'Provision Key', icon: <ShieldKeyholeRegular fontSize={14} /> },
  ];

  return (
    <div className={styles.layout}>
      <AppHeader admin={admin} onSignOut={onSignOut} />
      <div className={styles.content}>
        <div className={styles.pageTitle}>
          <Text className={styles.heading}>Provision FIDO2 Key</Text>
          <Text className={styles.subtitle}>
            Pre-provision a FIDO2 security key for a Microsoft Entra ID user
          </Text>
        </div>

        {/* Step indicator */}
        <div className={styles.stepper}>
          {steps.map((step, i) => {
            const isDone = currentStep > i;
            const isActive = currentStep === i;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', flexGrow: i < steps.length - 1 ? 1 : 0 }}>
                <div className={styles.step}>
                  <div
                    className={`${styles.stepIcon} ${
                      isDone ? styles.stepIconDone : isActive ? styles.stepIconActive : styles.stepIconPending
                    }`}
                  >
                    {isDone ? <CheckmarkCircleRegular fontSize={16} /> : step.icon}
                  </div>
                  <Text
                    className={`${styles.stepLabel} ${
                      isDone ? styles.stepLabelDone : isActive ? styles.stepLabelActive : styles.stepLabelPending
                    }`}
                  >
                    {step.label}
                  </Text>
                </div>
                {i < steps.length - 1 && (
                  <div className={`${styles.stepConnector} ${isDone ? styles.stepConnectorDone : ''}`} />
                )}
              </div>
            );
          })}
        </div>

        <UserLookup selectedUser={selectedUser} onUserSelected={setSelectedUser} />

        {selectedUser && (
          <PinGenerator pin={pin} onPinGenerated={setPin} />
        )}

        {selectedUser && pin && (
          <ProvisioningForm
            targetUser={selectedUser}
            pin={pin}
            admin={admin}
            onReset={handleReset}
            onProvisioned={() => setProvisioned(true)}
          />
        )}
      </div>
    </div>
  );
}
